import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { useBreakpointUp } from '@travel-frontend/core';
import { IconDateRange } from '@travel-frontend/icons';
import { useTranslation } from 'next-i18next';
import { forwardRef, useMemo } from 'react';
import { FieldChrome } from '../../../../components/SearchForm/components/FieldChrome/index.js';
import { useTravelDurationLabel } from '../../../../components/SearchForm/components/TravelPeriodField/hooks/useTravelDurationLabel.js';
import { useTravelPeriodLabel } from '../../../../components/SearchForm/components/TravelPeriodField/hooks/useTravelPeriodLabel.js';
import { fallbackDuration, getDaysInRange } from '../../../../components/SearchForm/components/TravelPeriodField/utils.js';
import { LabelMediumSemiBold, LabelSmall, Stack } from '../../../../primitives/index.js';
import { qaId } from '../../../../utils/index.js';
import { SEARCH_FORM_QA_ID } from '../../../../utils/qaId.js';
export const TravelPeriodField = forwardRef(function TravelPeriodField({
  value,
  active,
  disabled = false,
  defaultDuration,
  layout = 'full',
  ...restProps
}, ref) {
  const lgUp = useBreakpointUp('lg');
  const {
    t
  } = useTranslation();
  const days = useMemo(() => value ? getDaysInRange(value) : 0, [value]);
  const duration = useMemo(() => value?.duration ?? fallbackDuration(days, defaultDuration), [days, value, defaultDuration]);
  const travelPeriodLabel = useTravelPeriodLabel({
    startDate: value?.startDate,
    endDate: value?.endDate,
    short: true
  });
  const travelDurationLabel = useTravelDurationLabel({
    minDuration: duration?.min,
    maxDuration: duration?.max,
    short: layout === 'full' && lgUp
  });
  const label = [travelPeriodLabel, travelDurationLabel].filter(Boolean).join(', ');
  return _jsx(FieldChrome, {
    ...restProps,
    ref: ref,
    visuallyFocused: active,
    value: label,
    layout: layout,
    label: t('searchForm:travelPeriodField.placeholder'),
    "data-testid": "travel-period-field",
    "data-cy": qaId(SEARCH_FORM_QA_ID, 'travelPeriodField'),
    prependIcon: _jsx(IconDateRange, {}),
    children: _jsxs(Stack, {
      spacing: 0.75,
      direction: "row",
      alignItems: "center",
      children: [_jsx(LabelMediumSemiBold, {
        as: "span",
        "data-testid": qaId(SEARCH_FORM_QA_ID, 'travel', 'period'),
        children: travelPeriodLabel
      }), _jsx(LabelSmall, {
        as: "span",
        color: "contentSecondary",
        "data-testid": qaId(SEARCH_FORM_QA_ID, 'travel', 'duration'),
        children: travelDurationLabel
      })]
    })
  });
});